import React, { useEffect, useRef, useState } from "react";
import { Button, message, Modal, Tag, Typography } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { InstitutionCell } from "./InstitutionCell";
import type {
  Account,
  Connection,
} from "@/providers/smarta-banking/banking-api-client";
import { ApiPaths } from "@/providers/smarta-banking/banking-api-client";
import { Label, Tbody, Td, Tr, Value } from "../../../components/styles";
import { DISPLAY_DATE_TIME_FORMAT } from "@/v1/constants";
import { AccountRow } from "./AccountRow";
import { api } from "../../client";

const getTexts = (t: TFunction) => ({
  labels: {
    archivedAt: t("connectionTable.labels.archivedAt"),
    accessValidUntil: t("connectionTable.labels.accessValidUntil"),
    agreementAcceptedAt: t("connectionTable.labels.agreementAcceptedAt"),
    historicalStartDate: t("connectionTable.labels.historicalStartDate"),
    archived: t("connectionTable.labels.archived"),
    active: t("connectionTable.labels.active"),
  },
  archiveModal: {
    title: t("connectionTable.archiveModal.title"),
    content: t("connectionTable.archiveModal.content"),
    okText: t("connectionTable.archiveModal.okText"),
    cancelText: t("connectionTable.archiveModal.cancelText"),
  },
  archiveButton: {
    archive: t("connectionTable.archiveButton.archive"),
    archiving: t("connectionTable.archiveButton.archiving"),
  },
  messages: {
    archivalSuccess: t("connectionTable.messages.archivalSuccess"),
    archivalError: t("connectionTable.messages.archivalError"),
  },
});

export type ConnectionTableData = Connection & {
  type: "connection";
  accounts: Account[];
};

type Props = {
  data: ConnectionTableData;
  customerId: string;
};

export const ConnectionTable: React.FC<Props> = ({ data, customerId }) => {
  const { t } = useTranslation("Banking");
  const texts = getTexts(t);
  const queryClient = useQueryClient();
  const [isWaitingForRefresh, setIsWaitingForRefresh] = useState(false);
  const isMountedRef = useRef(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    id: connectionId,
    institutionId,
    agreementAcceptedAt,
    historicalStartDate,
    accessValidUntil,
    archivedAt,
    accounts,
  } = data;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const { mutate: archiveConnection, isPending: isArchiving } = api.useMutation(
    "post",
    ApiPaths.postApiCustomersByCustomerIdConnectionsByConnectionIdArchive,
    {
      onSuccess: async () => {
        message.success(texts.messages.archivalSuccess);
        setIsWaitingForRefresh(true);

        // Invalidate queries after 3 seconds to catch the updated archival state
        timeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            queryClient.invalidateQueries({
              queryKey: [
                "get",
                ApiPaths.getApiCustomersByCustomerIdConnections,
              ],
            });
            queryClient.invalidateQueries({
              queryKey: ["get", ApiPaths.getApiCustomersByCustomerIdAccounts],
            });
            setIsWaitingForRefresh(false);
          }
        }, 3000);
      },
      onError: (error) => {
        message.error(error.error?.message || texts.messages.archivalError);
      },
    }
  );

  const handleArchiveClick = () => {
    Modal.confirm({
      title: texts.archiveModal.title,
      icon: <ExclamationCircleOutlined />,
      content: texts.archiveModal.content,
      okText: texts.archiveModal.okText,
      cancelText: texts.archiveModal.cancelText,
      onOk: () => {
        archiveConnection({
          params: {
            path: {
              customerId,
              connectionId,
            },
          },
        });
      },
    });
  };

  return (
    <Tbody>
      <Tr isHeaderRow hasBorder>
        <InstitutionCell institutionId={institutionId} />
        <Td>
          <Label>
            {archivedAt
              ? texts.labels.archivedAt
              : texts.labels.accessValidUntil}
          </Label>
          <Value>
            {moment(archivedAt || accessValidUntil).format(
              DISPLAY_DATE_TIME_FORMAT
            )}
          </Value>
        </Td>
        <Td>
          <Label>{texts.labels.agreementAcceptedAt}</Label>
          <Value>
            {moment(agreementAcceptedAt).format(DISPLAY_DATE_TIME_FORMAT)}
          </Value>
        </Td>
        <Td>
          <Label>{texts.labels.historicalStartDate}</Label>
          <Value>
            {moment(historicalStartDate).format(DISPLAY_DATE_TIME_FORMAT)}
          </Value>
        </Td>
        <Td className="text-right">
          {archivedAt ? (
            <Tag>
              <Typography.Text type="secondary">
                {texts.labels.archived}
              </Typography.Text>
            </Tag>
          ) : (
            <Tag color="success">{texts.labels.active}</Tag>
          )}
        </Td>
        <Td className="text-right">
          {!archivedAt && (
            <Button
              type="link"
              size="small"
              onClick={handleArchiveClick}
              loading={isArchiving}
              disabled={isArchiving || isWaitingForRefresh}
            >
              {isArchiving
                ? texts.archiveButton.archiving
                : texts.archiveButton.archive}
            </Button>
          )}
        </Td>
      </Tr>
      {accounts.map((account) => (
        <AccountRow
          key={account.id}
          customerId={customerId}
          account={account}
        />
      ))}
    </Tbody>
  );
};
